import axiosInstance from 'config/axiosInstance';
import React, { useEffect, useState } from 'react';
import { IconEye, IconEdit } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import EditPopup from 'reusable/AddEditDelete/Edit';
import { useTranslation } from 'react-i18next';

export interface DriverInterface {
  id: number;
  name: string;
  submission: number;
  exceed: number;
  phone: string;
  email: string;
  birthday: string;
  create_time: string;
  profile_picture?: string;
  client_delivery?: number;
  device: number[];
}

interface DriverTableProps {
  driver: DriverInterface[];
}

const DriverTable: React.FC<DriverTableProps> = ({ driver }) => {
  const { t, i18n } = useTranslation('driver');
  const navigate = useNavigate();
  const [selectedDriver, setSelectedDriver] = useState<DriverInterface | null>(
    null
  );
  const [drivers, setDrivers] = useState<DriverInterface[]>(driver);
  const [deviceOptions, setDeviceOptions] = useState<any[]>([]);
  const [assignedDevices, setAssignedDevices] = useState<any[]>([]);

  const handleViewClick = (id: number) => {
    navigate(`/driver/${id}`);
  };

  const handleEditClick = async (driver: DriverInterface) => {
    setSelectedDriver(driver);
    const devices = await fetchAssignedDevices(driver.id);
    setAssignedDevices(devices);
  };
  const handleClosePopup = () => {
    setSelectedDriver(null);
  };

  const handleSave = async (updatedValues: { [key: string]: any }) => {
    console.log(updatedValues);
    if (updatedValues['Driver Name'] === undefined) {
      updatedValues['Driver Name'] = selectedDriver?.name;
    }

    const driverNameEngChanged = updatedValues['DRIVER NAME'] !== undefined && updatedValues['DRIVER NAME'].trim() !== selectedDriver?.name.trim();
    const driverNameJpnChanged = updatedValues['ドライバー名'] !== undefined && updatedValues['ドライバー名'].trim() !== selectedDriver?.name.trim();
    const emailAddressEngChanged = updatedValues['EMAIL ADDRESS'] !== undefined && updatedValues['EMAIL ADDRESS'].trim() !== selectedDriver?.email.trim();
    const emailAddressJpnChanged = updatedValues['メールアドレス'] !== undefined && updatedValues['メールアドレス'].trim() !== selectedDriver?.email.trim();

    const driverNameChanged = driverNameEngChanged || driverNameJpnChanged;
    const emailAddressChanged = emailAddressEngChanged || emailAddressJpnChanged;

    if (driverNameChanged) {
        const newDriverName = updatedValues['DRIVER NAME']?.trim() || updatedValues['ドライバー名']?.trim();
        if (!newDriverName) {
            alert('Driver Name cannot be empty.');
            return;
        }
    }
    if (emailAddressChanged) {
        const newEmailAddress = updatedValues['EMAIL ADDRESS']?.trim() || updatedValues['メールアドレス']?.trim();
        if (!newEmailAddress) {
            alert('Email Address cannot be empty.');
            return;
        }
    }
    
    const assignedNewDevices = updatedValues['ASSIGNED DEVICE'] || [];
    const newDeviceIds = assignedNewDevices.map((device: any) => device.id);

    function getValueByLanguage<T>(object: T, englishKey: keyof T, japaneseKey: keyof T): T[keyof T] {
      return object[japaneseKey] ?? object[englishKey];
    }    

    const updatedDriver: DriverInterface = {
      id: updatedValues.id,
      name: getValueByLanguage(updatedValues, 'DRIVER NAME', 'ドライバー名'),
      submission: selectedDriver?.submission || 0,
      exceed: selectedDriver?.exceed || 0,
      phone: getValueByLanguage(updatedValues, 'CONTACT NUMBER', '電話番号'),
      email: getValueByLanguage(updatedValues, 'EMAIL ADDRESS', 'メールアドレス'),
      birthday: getValueByLanguage(updatedValues, 'DATE OF BIRTH', '生年月日'),
      create_time: updatedValues.create_time,
      profile_picture: '',
      device: newDeviceIds,
    };
    
    const driverPatchBody = {
      name: updatedDriver.name,
      birthday: updatedDriver.birthday,
      phone: updatedDriver.phone,
      email: updatedDriver.email,
    };

    try {
      await axiosInstance.patch(
        `/v1/drivers/${updatedDriver.id}/`,
        driverPatchBody
      );

      const assignedDeviceIds = assignedDevices.map((device: any) => device.id);

      const devicesToAssign = newDeviceIds.filter(
        (id: number) => !assignedDeviceIds.includes(id)
      );
      const devicesToUnassign = assignedDeviceIds.filter(
        (id: number) => !newDeviceIds.includes(id)
      );

      for (const deviceId of devicesToAssign) {
        await axiosInstance.patch(`/v1/devices/${deviceId}/`, {
          status: 'using',
          user: updatedValues.id,
        });
      }

      for (const deviceId of devicesToUnassign) {
        await axiosInstance.patch(`/v1/devices/${deviceId}/`, {
          status: 'available',
          user: null,
        });
      }
      setDrivers((prevDrivers) =>
        prevDrivers.map((drv) =>
          drv.id === updatedDriver.id ? updatedDriver : drv
        )
      );
      setSelectedDriver(null);
    } catch (error) {
      console.error('Failed to update driver', error);
    }
    window.location.reload();
  };

  useEffect(() => {
    fetchDeviceData();
  }, []);

  const fetchDeviceData = async () => {
    let apiUrl = `/v1/devices/?page_size=0`;

    try {
      const responseDevice = await axiosInstance.get(apiUrl);

      const availableDevices = responseDevice.data.filter(
        (device: any) => device.user === null
      );
      console.log(availableDevices);
      setDeviceOptions(
        availableDevices.map((device: any) => ({
          name: device.name,
          id: device.id,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const fetchAssignedDevices = async (driverId: number) => {
    const apiUrl = `/v1/devices/?user=${driverId}`;

    try {
      const response = await axiosInstance.get(apiUrl);

      return response.data.results.map((device: any) => ({
        name: device.name,
        id: device.id,
      }));
    } catch (error) {
      console.error('Failed to fetch assigned devices', error);
      return [];
    }
  };

  const editFields = selectedDriver
    ? [
        {
          label: `${t('edit.driver-id')}`,
          name: 'id',
          type: 'text',
          value: selectedDriver.id.toString(),
          readOnly: true,
          required: true,
        },
        {
          label: `${t('edit.driver-name')}`,
          name: 'name',
          type: 'text',
          value: selectedDriver.name,
          required: true,
        },
        {
          label: `${t('edit.email')}`,
          name: 'email',
          type: 'email',
          value: selectedDriver.email,
          required: true,
        },
        {
          label: `${t('edit.dob')}`,
          name: 'birthday',
          type: 'calendar',
          value: selectedDriver.birthday,
        },
        {
          label: `${t('edit.assigned-device')}`,
          name: 'device',
          type: 'multi-select',
          options: deviceOptions,
          value: assignedDevices.map((device) =>
            JSON.stringify({ name: device.name, id: device.id })
          ),
        },
        {
          label: `${t('edit.contact')}`,
          name: 'phone',
          type: 'text',
          value: selectedDriver.phone,
        },
      ]
    : [];

  const formatDate = (datetime: string) => {
    const date = new Date(datetime);

    if (i18n.language === 'jp') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return {
        time: date.toLocaleTimeString('ja-JP', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        date: `${year}/${month}/${day}`,
      };
    } else {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const day = date.getDate().toString().padStart(2, '0');
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const localTimeString = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      return {
        time: localTimeString,
        date: `${day} ${months[monthIndex]} ${year}`,
      };
    }
  };

  return (
    <section className="flex flex-col bg-white rounded-md p-2">
      {/* Table Header */}
      <div className="flex flex-row bg-primary/50 rounded-md py-2 pl-2 pr-4 items-center gap-2 justify-between">
        <h6 className="w-1/12 text-left">{t('table.driver-id')}</h6>
        <h6 className="w-3/12 text-left">{t('table.driver-name')}</h6>
        <h6 className="w-1/12 text-center">{t('table.exceed-limits')}</h6>
        <h6 className="w-2/12 text-center">{t('table.contact')}</h6>
        <h6 className="w-2/12 text-left">{t('table.email')}</h6>
        <h6 className="w-2/12 text-center">{t('table.registered')}</h6>
        <h6 className="w-1/12 text-center">{t('table.action')}</h6>
      </div>
      {/* Table Body */}
      <div className="flex flex-col">
        {driver.map((drv) => (
          <div
            key={drv.id}
            className="flex flex-row py-2 pl-2 pr-4 border-b items-center justify-center gap-2"
          >
            <span className="w-1/12 text-left text-gray-500 py-3">
              #{drv.id}
            </span>
            <span className="w-3/12 text-left">{drv.name}</span>
            <span
              className={`w-1/12 text-center rounded-md py-1 ${
                drv.exceed !== 0
                  ? 'bg-[#FFE5EE] text-[#DE0000]'
                  : 'text-green-600 bg-primary/50'
              }`}
            >
              {drv.exceed !== 0
                ? `${drv.exceed} ${t('table.time')}`
                : t('table.na')}
            </span>
            <span className="w-2/12 text-center">{drv.phone}</span>
            <span
              className="w-2/12 text-left"
              style={{
                wordWrap: 'break-word',
                maxHeight: '3rem',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {drv.email}
            </span>
            <span className="w-2/12 text-center">
              {formatDate(drv.create_time).date}
            </span>
            <div className="w-1/12 text-center flex gap-3 justify-center text-neutral/100 cursor-pointer">
              <IconEye
                size={20}
                stroke={1.5}
                onClick={() => handleViewClick(drv.id)}
              />
              <IconEdit
                size={20}
                stroke={1.5}
                onClick={() => handleEditClick(drv)}
              />
            </div>
          </div>
        ))}
      </div>
      {selectedDriver && (
        <EditPopup
          title={t('edit.title')}
          fields={editFields}
          save={t('edit.save')}
          initialValues={selectedDriver}
          onSave={handleSave}
          onClose={handleClosePopup}
        />
      )}
    </section>
  );
};

export default DriverTable;